import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import PageMeta from '../../components/common/PageMeta';
import Button from '../../components/ui/button/Button';
import NMWellCard from '../../components/nm/NMWellCard';
import NMDocumentList from '../../components/nm/NMDocumentList';
import IndexingProgress from '../../components/research/IndexingProgress';
import CitationList from '../../components/research/CitationList';
import Badge from '../../components/ui/badge/Badge';
import {
  createResearchSession,
  getResearchSession,
  getResearchDocuments,
  getResearchChat,
  getResearchSummary,
  listResearchSessions,
  streamResearchAnswer,
  ResearchSession,
  ResearchMessage,
  ResearchDocument,
  RetrievedDocument,
  WellSummary,
  openAuthenticatedUrl,
} from '../../lib/api/research';
import { fetchNMWell, listNMDocuments, NMWellData, NMDocument } from '../../lib/api/nm';

type Phase = 'entry' | 'indexing' | 'ready';

const DOC_TYPE_LABELS: Record<string, string> = {
  w1: 'W-1 Drilling Permit',
  w2: 'W-2 Completion',
  w3: 'W-3 Plugging Record',
  w3a: 'W-3A Plugging Proposal',
  w15: 'W-15 Completions',
  w12: 'W-12 Cementing',
  g1: 'G-1 Organizational Report',
  gau: 'GAU Advisory',
  l1: 'L-1 Location',
  p14: 'P-14 Pressure Test',
  swr10: 'SWR-10',
  swr13: 'SWR-13',
  schematic: 'Well Schematic',
  formation_tops: 'Formation Tops',
  c_100: 'C-100 NM Well Record',
  c_101: 'C-101 NM Permit',
  c_102: 'C-102 NM Well Location Plat',
  c_103: 'C-103 NM Sundry Notice',
  c_104: 'C-104 NM Request for Allowable',
  c_105: 'C-105 NM Completion Report',
  sundry: 'Sundry Notice',
  apd: 'Application for Permit to Drill',
  pa_procedure: 'PA Procedure',
  unknown: 'Unclassified Document',
};

function docTypeLabel(raw: string): string {
  return DOC_TYPE_LABELS[raw] ?? raw.toUpperCase();
}

function detectState(apiNumber: string): string | null {
  const digits = apiNumber.replace(/\D/g, '');
  if (digits.startsWith('30')) return 'NM';
  if (digits.startsWith('42')) return 'TX';
  return null;
}

// ============================================================
// Well Summary Panel (collapsible, rendered above chat)
// ============================================================
function WellSummaryPanel({ summary }: { summary: WellSummary }) {
  const [collapsed, setCollapsed] = useState(false);
  const wi = summary.well_info;

  const infoItems: [string, string | number | undefined][] = [
    ['Operator', wi.operator],
    ['Field', wi.field],
    ['Lease', wi.lease],
    ['County', wi.county],
    ['District', wi.district],
    ['Well No', wi.well_no],
    ['Total Depth', wi.total_depth_ft ? `${wi.total_depth_ft.toLocaleString()} ft` : undefined],
  ];

  return (
    <div className="border-b border-gray-200 dark:border-white/[0.05]">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="h-4 w-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-sm font-semibold text-gray-800 dark:text-white/90">Well Summary</span>
          {summary.api_number && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{summary.api_number}</span>
          )}
        </div>
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${collapsed ? '' : 'rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!collapsed && (
        <div className="px-4 pb-3 space-y-3">
          {/* Well Info grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5">
            {infoItems
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([label, value]) => (
                <div key={label}>
                  <dt className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</dt>
                  <dd className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{value}</dd>
                </div>
              ))}
          </div>

          {/* Document counts as badges */}
          {Object.keys(summary.document_counts).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(summary.document_counts).map(([docType, count]) => (
                <span
                  key={docType}
                  className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400"
                >
                  {docTypeLabel(docType)} ({count})
                </span>
              ))}
            </div>
          )}

          {/* Casing Records */}
          {summary.casing_records.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 select-none">
                Casing Records ({summary.casing_records.length})
              </summary>
              <div className="mt-1.5 overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-white/[0.05] text-left text-gray-500 dark:text-gray-400">
                      <th className="pb-1 pr-3 font-medium">Type</th>
                      <th className="pb-1 pr-3 font-medium">Size (in)</th>
                      <th className="pb-1 pr-3 font-medium">Weight (ppf)</th>
                      <th className="pb-1 pr-3 font-medium">Top (ft)</th>
                      <th className="pb-1 pr-3 font-medium">Bottom (ft)</th>
                      <th className="pb-1 pr-3 font-medium">Hole Size (in)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.03]">
                    {summary.casing_records.map((c, i) => (
                      <tr key={i} className="text-gray-700 dark:text-gray-300">
                        <td className="py-0.5 pr-3">{c.string_type ?? '—'}</td>
                        <td className="py-0.5 pr-3">{c.size_in ?? '—'}</td>
                        <td className="py-0.5 pr-3">{c.weight_ppf ?? '—'}</td>
                        <td className="py-0.5 pr-3">{c.top_ft?.toLocaleString() ?? '—'}</td>
                        <td className="py-0.5 pr-3">{c.bottom_ft?.toLocaleString() ?? '—'}</td>
                        <td className="py-0.5 pr-3">{c.hole_size_in ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Plug Records */}
          {summary.plug_records.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 select-none">
                Plug Records ({summary.plug_records.length})
              </summary>
              <div className="mt-1.5 overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-white/[0.05] text-left text-gray-500 dark:text-gray-400">
                      <th className="pb-1 pr-3 font-medium">#</th>
                      <th className="pb-1 pr-3 font-medium">Type</th>
                      <th className="pb-1 pr-3 font-medium">Method</th>
                      <th className="pb-1 pr-3 font-medium">Class</th>
                      <th className="pb-1 pr-3 font-medium">Sacks</th>
                      <th className="pb-1 pr-3 font-medium">Top (ft)</th>
                      <th className="pb-1 pr-3 font-medium">Bottom (ft)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.03]">
                    {summary.plug_records.map((p, i) => (
                      <tr key={i} className="text-gray-700 dark:text-gray-300">
                        <td className="py-0.5 pr-3">{p.plug_number ?? '—'}</td>
                        <td className="py-0.5 pr-3">{p.plug_type ?? '—'}</td>
                        <td className="py-0.5 pr-3">{p.method ?? '—'}</td>
                        <td className="py-0.5 pr-3">{p.cement_class ?? '—'}</td>
                        <td className="py-0.5 pr-3">{p.sacks ?? '—'}</td>
                        <td className="py-0.5 pr-3">{p.depth_top_ft?.toLocaleString() ?? '—'}</td>
                        <td className="py-0.5 pr-3">{p.depth_bottom_ft?.toLocaleString() ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Filing Timeline */}
          {summary.filing_timeline.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 select-none">
                Filing Timeline ({summary.filing_timeline.length})
              </summary>
              <div className="mt-1.5 space-y-1 max-h-60 overflow-y-auto">
                {summary.filing_timeline.slice(0, 20).map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300"
                  >
                    <span className="w-20 flex-shrink-0 text-gray-500 dark:text-gray-400">
                      {f.date ?? '—'}
                    </span>
                    <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {docTypeLabel(f.document_type)}
                    </span>
                    {f.tracking_no && (
                      <span className="text-gray-400 dark:text-gray-500">#{f.tracking_no}</span>
                    )}
                    {f.operator && (
                      <span className="truncate text-gray-400 dark:text-gray-500">{f.operator}</span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Lease Wells Panel (other wells on the same Neubus lease)
// ============================================================
function LeaseWellsPanel({
  siblings,
  onResearch,
}: {
  siblings: ResearchSession['lease_sibling_wells'];
  onResearch: (api14: string) => void;
}) {
  if (!siblings || siblings.length === 0) return null;

  const formatApi = (api14: string) => {
    if (api14.length === 14) {
      return `${api14.slice(0, 2)}-${api14.slice(2, 5)}-${api14.slice(5, 10)}-${api14.slice(10)}`;
    }
    return api14;
  };

  const statusConfig: Record<string, { label: string; className: string }> = {
    cold_storage: {
      label: 'Available',
      className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    },
    indexing: {
      label: 'Indexing...',
      className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
    },
    ready: {
      label: 'Ready',
      className: 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400',
    },
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-white/[0.05]">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
          Other Wells on This Lease
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {siblings.length} additional well{siblings.length !== 1 ? 's' : ''} found during document triage
        </p>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-white/[0.03]">
        {siblings.map((s) => {
          const cfg = statusConfig[s.data_status] || statusConfig.cold_storage;
          return (
            <div
              key={s.api14}
              className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-white/[0.03]"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {formatApi(s.api14)}
                </span>
                {s.well_number && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Well #{s.well_number}
                  </span>
                )}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.className}`}>
                  {cfg.label}
                </span>
              </div>
              <button
                onClick={() => onResearch(s.api14)}
                disabled={s.data_status === 'indexing'}
                className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {s.data_status === 'ready' ? 'View' : 'Research'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Research() {
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const [searchParams] = useSearchParams();
  const urlApi14 = searchParams.get('api14');
  const navigate = useNavigate();

  // Phase 1 state
  const [phase, setPhase] = useState<Phase>('entry');
  const [apiInput, setApiInput] = useState('');
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Session state
  const [session, setSession] = useState<ResearchSession | null>(null);
  const [, setDocuments] = useState<ResearchDocument[]>([]);
  const [nmDocuments, setNmDocuments] = useState<NMDocument[]>([]);
  const [retrievedDocs, setRetrievedDocs] = useState<RetrievedDocument[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [rememberedDocuments, setRememberedDocuments] = useState<number>(0);
  const [nmDocsLoading, setNmDocsLoading] = useState(false);
  const [wellData, setWellData] = useState<NMWellData | null>(null);
  const [wellSummary, setWellSummary] = useState<WellSummary | null>(null);

  // Chat state
  const [messages, setMessages] = useState<ResearchMessage[]>([]);
  const [questionInput, setQuestionInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [docsCollapsed, setDocsCollapsed] = useState(false);

  // Session history state
  const [sessions, setSessions] = useState<ResearchSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  // Polling interval ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  // Guard: only fire the well-detail redirect once per completion
  const completionRedirectedRef = useRef(false);
  // Aborts the in-flight streamResearchAnswer fetch when the user cancels
  const abortControllerRef = useRef<AbortController | null>(null);

  const detectedState = detectState(apiInput);

  // Stop polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // On mount: load session from URL if present, or auto-fill api14 from query param
  useEffect(() => {
    if (urlSessionId) {
      // Deep-link: load session directly from URL
      getResearchSession(urlSessionId)
        .then((sess) => {
          setSession(sess);
          if (sess.status === 'ready') {
            setPhase('ready');
            getResearchChat(urlSessionId)
              .then((data) => setMessages(data))
              .catch((err) => console.error('[Research] Chat load error:', err));
            getResearchDocuments(urlSessionId)
              .then((data) => {
                setRetrievedDocs(data.retrieved_documents || []);
                setTotalDocuments(data.total_documents ?? 0);
                setRememberedDocuments(data.remembered_documents ?? 0);
              })
              .catch((err) => console.error('[Research] Docs load error:', err));
            getResearchSummary(urlSessionId)
              .then(setWellSummary)
              .catch((err) => console.error('[Research] Summary load error:', err));
            if (sess.state === 'NM') {
              fetchNMWell(sess.api_number).then(setWellData).catch(() => {});
              listNMDocuments(sess.api_number).then(r => setNmDocuments(r.documents)).catch(() => {});
            }
          } else if (['pending', 'fetching', 'indexing'].includes(sess.status)) {
            setPhase('indexing');
            if (sess.state === 'NM') {
              fetchNMWell(sess.api_number).then(setWellData).catch(() => {});
              listNMDocuments(sess.api_number).then(r => setNmDocuments(r.documents)).catch(() => {});
            }
            startPolling(urlSessionId);
          }
        })
        .catch((err) => {
          console.error('[Research] Failed to load session from URL:', err);
        });
    } else if (urlApi14) {
      // Auto-start research session — navigated here from Well Registry
      const autoStart = async () => {
        setStarting(true);
        try {
          const state = detectState(urlApi14) ?? undefined;
          const sess = await createResearchSession(urlApi14, state);
          setSession(sess);
          navigate(`/research/${sess.id}`, { replace: true });
          if (sess.status === 'ready') {
            const chat = await getResearchChat(sess.id);
            setMessages(chat);
            getResearchDocuments(sess.id)
              .then(r => {
                setRetrievedDocs(r.retrieved_documents || []);
                setTotalDocuments(r.total_documents ?? 0);
                setRememberedDocuments(r.remembered_documents ?? 0);
              })
              .catch(console.error);
            getResearchSummary(sess.id).then(setWellSummary).catch(console.error);
            setPhase('ready');
          } else {
            setPhase('indexing');
            startPolling(sess.id);
          }
          if (state === 'NM') {
            fetchNMWell(urlApi14).then(setWellData).catch(() => {});
            listNMDocuments(urlApi14).then(r => setNmDocuments(r.documents)).catch(() => {});
          }
        } catch (err) {
          setStartError(err instanceof Error ? err.message : 'Failed to start research');
        } finally {
          setStarting(false);
        }
      };
      autoStart();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load session history on mount
  useEffect(() => {
    listResearchSessions()
      .then(setSessions)
      .catch((err) => console.error('[Research] Failed to load sessions:', err))
      .finally(() => setSessionsLoading(false));
  }, []);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startPolling = (sessionId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const updated = await getResearchSession(sessionId);
        setSession(updated);
        if (updated.status === 'ready') {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          // Redirect to the well detail page with the Research tab pre-selected.
          // Strip non-digits from api_number to obtain the normalized 14-digit api14.
          if (!completionRedirectedRef.current) {
            completionRedirectedRef.current = true;
            const api14 = updated.api_number.replace(/\D/g, '');
            navigate(`/well/${api14}?tab=Research`, { replace: true });
          }
          setPhase('ready');
          // Load supporting data; each failure is isolated and non-blocking.
          getResearchChat(sessionId)
            .then(setMessages)
            .catch((err) => console.error('[Research] Chat fetch error:', err));
          getResearchDocuments(sessionId)
            .then((resp) => {
              setRetrievedDocs(resp.retrieved_documents || []);
              setTotalDocuments(resp.total_documents ?? 0);
              setRememberedDocuments(resp.remembered_documents ?? 0);
            })
            .catch((err) => console.error('[Research] Docs fetch error:', err));
          getResearchSummary(sessionId)
            .then(setWellSummary)
            .catch((err) => console.error('[Research] Summary fetch error:', err));
        } else if (updated.status === 'error') {
          clearInterval(pollRef.current!);
          pollRef.current = null;
        }
      } catch (err) {
        console.error('[Research] Poll error:', err);
      }
    }, 2000);
  };

  const handleResumeSession = async (s: ResearchSession) => {
    setSession(s);
    navigate(`/research/${s.id}`, { replace: true });
    if (s.status === 'ready') {
      const chat = await getResearchChat(s.id);
      setMessages(chat);
      getResearchSummary(s.id)
        .then(setWellSummary)
        .catch((err) => console.error('[Research] Summary fetch error:', err));
      setPhase('ready');
    } else if (s.status === 'indexing' || s.status === 'fetching' || s.status === 'pending') {
      setPhase('indexing');
      startPolling(s.id);
    }
    if (s.state === 'NM') {
      fetchNMWell(s.api_number).then(setWellData).catch(() => {});
      listNMDocuments(s.api_number).then(r => setNmDocuments(r.documents)).catch(() => {});
    }
  };

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = apiInput.trim();
    if (!trimmed) {
      setStartError('Please enter an API number');
      return;
    }

    setStarting(true);
    setStartError(null);

    try {
      const state = detectedState ?? undefined;
      const newSession = await createResearchSession(trimmed, state);
      setSession(newSession);
      navigate(`/research/${newSession.id}`, { replace: true });
      setPhase('indexing');

      // Fetch NM-specific data in parallel
      if (state === 'NM' || newSession.state === 'NM') {
        setNmDocsLoading(true);
        fetchNMWell(trimmed)
          .then((well) => {
            // Validate we got real data, not garbage from CAPTCHA pages
            if (well && well.well_name && well.well_name.length > 1 && well.operator_name) {
              setWellData(well);
            } else {
              console.warn('[Research] NM well data appears incomplete, skipping well card display');
              setWellData(null);
            }
          })
          .catch((err) => {
            console.error('[Research] NM well fetch error:', err);
            setWellData(null);
          });

        listNMDocuments(trimmed)
          .then((resp) => setNmDocuments(resp.documents))
          .catch((err) => console.error('[Research] NM docs error:', err))
          .finally(() => setNmDocsLoading(false));
      }

      // If already ready (unlikely but handle it)
      if (newSession.status === 'ready') {
        const chat = await getResearchChat(newSession.id);
        setMessages(chat);
        // Fetch extracted documents for display
        getResearchDocuments(newSession.id)
          .then((resp) => {
            setRetrievedDocs(resp.retrieved_documents || []);
            setTotalDocuments(resp.total_documents ?? 0);
            setRememberedDocuments(resp.remembered_documents ?? 0);
          })
          .catch((err) => console.error('[Research] Docs fetch error:', err));
        // Fetch well summary
        getResearchSummary(newSession.id)
          .then(setWellSummary)
          .catch((err) => console.error('[Research] Summary fetch error:', err));
        setPhase('ready');
      } else {
        startPolling(newSession.id);
      }
    } catch (err) {
      console.error('[Research] Start error:', err);
      setStartError(err instanceof Error ? err.message : 'Failed to start research session');
    } finally {
      setStarting(false);
    }
  };

  const handleReset = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    navigate('/research', { replace: true });
    setPhase('entry');
    setApiInput('');
    setSession(null);
    setDocuments([]);
    setNmDocuments([]);
    setRetrievedDocs([]);
    setTotalDocuments(0);
    setRememberedDocuments(0);
    setWellData(null);
    setWellSummary(null);
    setMessages([]);
    setStartError(null);
    setDocsCollapsed(false);
  };

  const handleResearchSibling = (api14: string) => {
    handleReset();
    // Small delay to let state clear, then auto-fill the API input
    setTimeout(() => {
      setApiInput(api14);
    }, 100);
  };

  const submitQuestion = async () => {
    const question = questionInput.trim();
    if (!question || !session || asking) return;

    setQuestionInput('');
    setAsking(true);

    // Optimistically add user message
    const userMsg: ResearchMessage = {
      id: `local-${Date.now()}`,
      role: 'user',
      content: question,
      citations: [],
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    // Add placeholder assistant message for streaming
    const assistantId = `streaming-${Date.now()}`;
    const assistantMsg: ResearchMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      citations: [],
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, assistantMsg]);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const stream = streamResearchAnswer(session.id, question, 8, controller.signal);
      for await (const event of stream) {
        if (event.type === 'token' && event.content) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: m.content + event.content } : m
            )
          );
        } else if (event.type === 'citations' && event.citations) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, citations: event.citations! } : m
            )
          );
        } else if (event.type === 'done') {
          // Reload chat to get server-assigned IDs
          const chat = await getResearchChat(session.id);
          setMessages(chat);
          break;
        } else if (event.type === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: event.message || 'An error occurred while generating the answer.' }
                : m
            )
          );
          break;
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled — leave whatever partial answer streamed in, with a subtle note.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: m.content ? `${m.content}\n\n_Cancelled._` : '_Cancelled._' }
              : m
          )
        );
      } else {
        console.error('[Research] Ask error:', err);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: err instanceof Error ? err.message : 'Failed to get answer.' }
              : m
          )
        );
      }
    } finally {
      abortControllerRef.current = null;
      setAsking(false);
    }
  };

  const handleAsk = (e: React.FormEvent) => {
    e.preventDefault();
    submitQuestion();
  };

  const isNM = session?.state === 'NM' || detectedState === 'NM';

  // ============================================================
  // Phase 1: Entry
  // ============================================================
  if (phase === 'entry') {
    return (
      <>
        <PageMeta title="Research | RegulAgent" description="AI-powered well research" />
        <PageBreadcrumb pageTitle="Research" />

        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Start Research Session
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Enter an API number to fetch and index regulatory documents for AI-assisted research.
              </p>
            </div>

            <div className="px-6 py-4">
              <form onSubmit={handleStart} className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label
                    htmlFor="api-input"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    API Number
                  </label>
                  <div className="relative">
                    <input
                      id="api-input"
                      type="text"
                      value={apiInput}
                      onChange={(e) => setApiInput(e.target.value)}
                      placeholder="e.g., 30-015-28692 or 42-123-45678"
                      className="w-full h-[42px] rounded-lg border border-gray-300 bg-transparent px-4 pr-24 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                      disabled={starting}
                    />
                    {detectedState && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">
                        {detectedState}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    30= New Mexico, 42= Texas. State is auto-detected from prefix.
                  </p>
                </div>
                <Button type="submit" disabled={starting || !apiInput.trim()}>
                  {starting ? (
                    <>
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                      Starting...
                    </>
                  ) : (
                    'Start Research'
                  )}
                </Button>
              </form>

              {startError && (
                <div className="mt-4 rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
                  <p className="text-sm text-error-700 dark:text-error-400">{startError}</p>
                </div>
              )}
            </div>
          </div>

          {/* Past Sessions */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Past Sessions
              </h3>
            </div>

            {sessionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center max-w-md">
                  <h4 className="mt-4 text-lg font-medium text-gray-700 dark:text-gray-300">
                    No sessions yet
                  </h4>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Select a well from the Well Registry to start a research session.
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-white/[0.05]">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        API Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        State
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Documents
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-white/[0.05]">
                    {sessions.map((s) => (
                      <tr
                        key={s.id}
                        onClick={() => handleResumeSession(s)}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-medium text-gray-800 dark:text-white/90">
                          {s.api_number}
                        </td>
                        <td className="px-6 py-4">
                          <Badge size="sm" color="primary">
                            {s.state}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          {s.status === 'ready' && (
                            <Badge size="sm" color="success">{s.status}</Badge>
                          )}
                          {s.status === 'indexing' && (
                            <Badge size="sm" color="warning">{s.status}</Badge>
                          )}
                          {s.status === 'fetching' && (
                            <Badge size="sm" color="warning">{s.status}</Badge>
                          )}
                          {s.status === 'pending' && (
                            <Badge size="sm" color="info">{s.status}</Badge>
                          )}
                          {s.status === 'error' && (
                            <Badge size="sm" color="error">{s.status}</Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(s.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {s.total_documents > 0
                            ? `${s.indexed_documents - s.failed_documents} / ${s.total_documents}`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ============================================================
  // Phase 2: Indexing
  // ============================================================
  if (phase === 'indexing' && session) {
    const api14 = session.api_number ? session.api_number.replace(/\D/g, '') : null;
    return (
      <>
        <PageMeta title="Research | RegulAgent" description="AI-powered well research" />
        <PageBreadcrumb pageTitle="Research" />

        <div className="space-y-4">
          {/* Header bar with API + reset */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {session.api_number}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Preparing research session...
              </p>
            </div>
            <div className="flex items-center gap-2">
              {api14 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/well/${api14}?tab=Research`)}
                >
                  Back to Well
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleReset}>
                New Search
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <IndexingProgress
            total={session.total_documents}
            indexed={session.indexed_documents}
            failed={session.failed_documents}
            status={session.status}
          />

          {/* Error state */}
          {session.status === 'error' && (
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                Indexing failed
              </p>
              {session.error_message && (
                <p className="mt-1 text-xs text-error-600 dark:text-error-500">
                  {session.error_message}
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                You can upload your own documents on the{' '}
                {api14 ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/well/${api14}?tab=Research`)}
                    className="text-blue-600 underline"
                  >
                    Well Detail page
                  </button>
                ) : (
                  <span>Well Detail page</span>
                )}
                .
              </p>
            </div>
          )}

          {/* Lease sibling wells panel (shown on error so user can try another API) */}
          {session.status === 'error' && session.lease_sibling_wells?.length > 0 && (
            <LeaseWellsPanel
              siblings={session.lease_sibling_wells}
              onResearch={handleResearchSibling}
            />
          )}

          {/* Split layout: docs left, well card right */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Documents */}
            {isNM && (
              <NMDocumentList
                api={session.api_number}
                documents={nmDocuments}
                loading={nmDocsLoading}
              />
            )}

            {/* Well card (NM only) */}
            {isNM && wellData && (
              <NMWellCard well={wellData} />
            )}

            {/* Placeholder when no well card yet */}
            {isNM && !wellData && (
              <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-r-transparent" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading well data...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ============================================================
  // Phase 3: Ready (Chat)
  // ============================================================
  if (phase === 'ready' && session) {
    const api14 = session.api_number ? session.api_number.replace(/\D/g, '') : null;
    return (
      <>
        <PageMeta title="Research | RegulAgent" description="AI-powered well research" />
        <PageBreadcrumb pageTitle="Research" />

        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                {session.api_number}
                {wellData?.well_name && (
                  <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                    — {wellData.well_name}
                  </span>
                )}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {session.indexed_documents - session.failed_documents} documents indexed
              </p>
            </div>
            <div className="flex items-center gap-2">
              {api14 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/well/${api14}?tab=Research`)}
                >
                  Back to Well
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleReset}>
                New Search
              </Button>
            </div>
          </div>

          {/* Lease sibling wells panel */}
          {session.lease_sibling_wells?.length > 0 && (
            <LeaseWellsPanel
              siblings={session.lease_sibling_wells}
              onResearch={handleResearchSibling}
            />
          )}

          {/* Main split layout */}
          <div className="flex gap-4">
            {/* Left panel: Documents (collapsible) */}
            <div className={`transition-all duration-300 ${docsCollapsed ? 'w-10' : 'w-80'} flex-shrink-0`}>
              {docsCollapsed ? (
                <button
                  onClick={() => setDocsCollapsed(false)}
                  className="flex h-full min-h-48 w-10 items-center justify-center rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 dark:border-white/[0.05] dark:bg-white/[0.03] dark:hover:bg-white/[0.05]"
                  title="Show documents"
                >
                  <svg
                    className="h-4 w-4 text-gray-400 rotate-90"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/[0.05]">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                        Documents ({isNM ? nmDocuments.length : totalDocuments})
                        {!isNM && rememberedDocuments > 0 && (
                          <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                            · {rememberedDocuments} remembered
                          </span>
                        )}
                      </h3>
                    </div>
                    <button
                      onClick={() => setDocsCollapsed(true)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Collapse"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  </div>
                  <div className="max-h-[600px] overflow-y-auto">
                    {isNM && (
                      <NMDocumentList
                        api={session.api_number}
                        documents={nmDocuments}
                        loading={false}
                      />
                    )}
                    {!isNM && retrievedDocs.length > 0 && (
                      <div className="px-4 py-3">
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Retrieved Documents
                        </h4>
                        <div className="space-y-1.5">
                          {retrievedDocs.map((doc) => {
                            const label = doc.document_type
                              ? docTypeLabel(doc.document_type)
                              : doc.filename;
                            const statusBadge = doc.remembered
                              ? { cls: 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400', text: 'remembered' }
                              : doc.index_status === 'pending'
                                ? { cls: 'bg-gray-100 text-gray-500 dark:bg-white/[0.05] dark:text-gray-400', text: 'pending' }
                                : { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400', text: 'not memorized' };
                            return (
                              <button
                                key={doc.id}
                                onClick={() => {
                                  // Absolute URLs open directly; API-relative ones go through the
                                  // authenticated fetch→blob helper (shared with citation chips).
                                  if (doc.download_url?.startsWith('http')) {
                                    window.open(doc.download_url, '_blank');
                                    return;
                                  }
                                  openAuthenticatedUrl(doc.download_url);
                                }}
                                className="flex items-center justify-between w-full py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-white/[0.03] cursor-pointer text-left transition-colors"
                                title={doc.filename}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <svg className="h-4 w-4 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                    {label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${statusBadge.cls}`}>
                                    {statusBadge.text}
                                  </span>
                                  <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right panel: Chat */}
            <div className="flex-1 flex flex-col rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] overflow-hidden">
              {/* Disclaimer banner */}
              <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Answers are generated from regulatory filings and may contain errors. Always verify
                  critical information against source documents.
                </p>
              </div>

              {/* Well Summary */}
              {wellSummary && <WellSummaryPanel summary={wellSummary} />}

              {/* Message list */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0" style={{ maxHeight: '500px' }}>
                {messages.length === 0 && (
                  <div className="flex items-center justify-center h-full py-12">
                    <div className="text-center">
                      <svg
                        className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                        Ask a question about this well
                      </p>
                    </div>
                  </div>
                )}

                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                        msg.role === 'user'
                          ? 'bg-brand-500 text-white'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content || (
                        <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-current opacity-60" />
                      )}</p>
                      {msg.role === 'assistant' && msg.citations.length > 0 && (
                        <CitationList citations={msg.citations} />
                      )}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-gray-200 dark:border-white/[0.05] px-4 py-3">
                <form onSubmit={handleAsk} className="flex items-end gap-2">
                  <textarea
                    value={questionInput}
                    onChange={(e) => setQuestionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        submitQuestion();
                      }
                    }}
                    placeholder="Ask a question about this well..."
                    disabled={asking}
                    rows={1}
                    className="flex-1 min-h-[42px] max-h-32 resize-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                  />
                  {asking ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => abortControllerRef.current?.abort()}
                    >
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-r-transparent" />
                      Cancel
                    </Button>
                  ) : (
                    <Button type="submit" disabled={!questionInput.trim()}>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </Button>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Well card below (NM only) */}
          {isNM && wellData && (
            <NMWellCard well={wellData} />
          )}
        </div>
      </>
    );
  }

  // Fallback (shouldn't happen)
  return null;
}
